import { useEffect, useState } from "react";
import { Facebook, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";

// Normalize / validate any Facebook URL the business pastes.
// Returns a clean URL or null if not a valid public Facebook URL.
export function normalizeFacebookUrl(raw) {
  if (!raw || typeof raw !== "string") return null;
  let url = raw.trim();
  if (!url) return null;
  // Strip extra whitespace + trailing slash
  url = url.replace(/\s+/g, "");
  // Prepend https:// if missing
  if (!/^https?:\/\//i.test(url)) url = "https://" + url;
  try {
    const u = new URL(url);
    let host = u.hostname.toLowerCase();
    // Accept facebook.com / www.facebook.com / m.facebook.com
    if (!/(^|\.)facebook\.com$/.test(host) && host !== "fb.com") return null;
    // Always use www.facebook.com for the embed
    u.hostname = "www.facebook.com";
    // Reject reels, watch, events, groups, posts paths (Page Plugin only supports Pages)
    const path = u.pathname.replace(/\/+$/, "");
    if (!path || path === "" || path === "/") return null;
    const blocked = ["/watch", "/reel", "/reels", "/events", "/groups", "/marketplace", "/messages"];
    if (blocked.some((b) => path.startsWith(b))) return null;
    return u.origin + path + u.search;
  } catch {
    return null;
  }
}

export default function FacebookPageEmbed({ pageUrl, businessName, compact = false }) {
  const cleanUrl = normalizeFacebookUrl(pageUrl);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  // Fallback timeout — if iframe doesn't fire onLoad within 6s, show the fallback
  useEffect(() => {
    if (!cleanUrl || loaded || failed) return;
    const t = setTimeout(() => { if (!loaded) setFailed(true); }, 6000);
    return () => clearTimeout(t);
  }, [cleanUrl, loaded, failed]);

  if (!cleanUrl) return null;

  const height = compact ? 480 : 600;
  const iframeSrc = `https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(cleanUrl)}&tabs=timeline&width=500&height=${height}&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=false`;

  return (
    <div className="glass-card p-6 rounded-2xl" data-testid="facebook-embed">
      <h2 className="text-lg font-bold font-['Outfit'] text-white mb-3 flex items-center gap-2">
        <Facebook className="w-5 h-5 text-[#1877F2]" />
        Recent Updates {businessName ? `from ${businessName}` : "on Facebook"}
      </h2>

      {!failed ? (
        <>
          <div className="rounded-xl overflow-hidden bg-white/5 flex justify-center" style={{ minHeight: height - 80 }}>
            <iframe
              title={`Facebook Page · ${businessName || "Business"}`}
              src={iframeSrc}
              style={{ border: "none", overflow: "hidden", maxWidth: 500, width: "100%", height }}
              scrolling="no"
              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
              allowFullScreen
              onLoad={() => setLoaded(true)}
              onError={() => setFailed(true)}
              data-testid="facebook-embed-iframe"
            />
          </div>
          {/* Always-visible "Open on Facebook" link as backup — works even if FB silently returns blank */}
          <div className="mt-3 text-center">
            <a
              href={cleanUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-[#1877F2] hover:text-[#3a8df0] font-medium"
              data-testid="facebook-embed-open-link"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Open on Facebook
            </a>
          </div>
        </>
      ) : (
        <div className="rounded-xl bg-white/5 p-6 text-center" data-testid="facebook-embed-fallback">
          <Facebook className="w-10 h-10 text-[#1877F2] mx-auto mb-3" />
          <p className="text-white font-medium mb-1">Visit on Facebook</p>
          <p className="text-[#94A3B8] text-sm mb-4">
            See {businessName ? `${businessName}'s` : "the latest"} Facebook updates directly on Facebook.
          </p>
          <a href={cleanUrl} target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#1877F2] hover:bg-[#1364D2] text-white" data-testid="facebook-fallback-btn">
              <ExternalLink className="w-4 h-4 mr-2" /> Open Facebook Page
            </Button>
          </a>
        </div>
      )}
    </div>
  );
}
